/**
 * GTM DataLayer tracking utility
 * Pushes events to window.dataLayer if GTM is present
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    MOMENTJE_EVENT_PREFIX?: string;
  }
}

// Default prefix for all Momentje tracking events
const DEFAULT_EVENT_PREFIX = 'mj_';

/**
 * Get the event prefix. Can be customized by setting window.MOMENTJE_EVENT_PREFIX
 * before the widget loads.
 */
function getEventPrefix(): string {
  if (typeof window !== 'undefined' && window.MOMENTJE_EVENT_PREFIX !== undefined) {
    return window.MOMENTJE_EVENT_PREFIX;
  }
  return DEFAULT_EVENT_PREFIX;
}

export function trackEvent(eventName: string, data?: Record<string, unknown>) {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    const prefix = getEventPrefix();
    const readableName = eventName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    window.dataLayer.push({
      event: `${prefix}${eventName}`,
      eventName: readableName,
      ...data,
    });
  }
}

// Widget lifecycle events
export function trackWidgetLoad(hotelId: string, roomId: string, loadTimeMs: number) {
  trackEvent('widget_load', { hotel_id: hotelId, room_id: roomId, load_time_ms: loadTimeMs });
}

export function trackWidgetError(errorMessage: string, hotelId: string, roomId?: string) {
  trackEvent('widget_error', { error_message: errorMessage, hotel_id: hotelId, room_id: roomId });
}

// Date selection events
export function trackSelectArrivalDate(date: string, hotelId: string, roomId: string) {
  trackEvent('select_arrival_date', { date, hotel_id: hotelId, room_id: roomId });
}

export function trackSelectDepartureDate(date: string, arrivalDate: string, nights: number, hotelId: string, roomId: string) {
  trackEvent('select_departure_date', { date, arrival_date: arrivalDate, nights, hotel_id: hotelId, room_id: roomId });
}

export function trackClearDateSelection(hotelId: string, roomId: string) {
  trackEvent('clear_date_selection', { hotel_id: hotelId, room_id: roomId });
}

// Conversion events
export function trackBeginCheckout(
  estimatedTotal: number,
  currency: string,
  arrivalDate: string,
  departureDate: string,
  nights: number,
  hotelId: string,
  roomId: string
) {
  trackEvent('begin_checkout', {
    estimated_total: estimatedTotal,
    currency,
    arrival_date: arrivalDate,
    departure_date: departureDate,
    nights,
    hotel_id: hotelId,
    room_id: roomId,
  });
}

export function trackViewPricesClick(hotelId: string, roomId: string) {
  trackEvent('view_prices_click', { hotel_id: hotelId, room_id: roomId });
}

// Engagement events
export function trackNavigateMonth(targetMonth: string, hotelId: string, roomId: string) {
  trackEvent('navigate_month', { target_month: targetMonth, hotel_id: hotelId, room_id: roomId });
}

// Friction events
export function trackClickUnavailableDate(date: string, reason: string, hotelId: string, roomId: string) {
  trackEvent('click_unavailable_date', { date, reason, hotel_id: hotelId, room_id: roomId });
}

export function trackMinimumStayError(selectedNights: number, requiredNights: number, hotelId: string, roomId: string) {
  trackEvent('minimum_stay_error', { selected_nights: selectedNights, required_nights: requiredNights, hotel_id: hotelId, room_id: roomId });
}

// Price display event
export function trackViewPrice(estimatedTotal: number, currency: string, nights: number, hotelId: string, roomId: string) {
  trackEvent('view_price', { estimated_total: estimatedTotal, currency, nights, hotel_id: hotelId, room_id: roomId });
}

// Hotel overview events
export function trackViewHotel(hotelId: string, hotelName: string) {
  trackEvent('view_hotel', { hotel_id: hotelId, hotel_name: hotelName });
}

export function trackSelectRoom(hotelId: string, roomId: string, roomName: string) {
  trackEvent('select_room', { hotel_id: hotelId, room_id: roomId, room_name: roomName });
}

export function trackExpandRoomDetails(hotelId: string, roomId: string, roomName: string) {
  trackEvent('expand_room_details', { hotel_id: hotelId, room_id: roomId, room_name: roomName });
}
